import { useFormik } from 'formik'
import type { TFunction } from 'i18next'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'

import { useCandidates } from '../../candidatesContext/useCandidatesContext'
import { useRegistryLookup } from '../../hooks/useRegistryLookup'
import { OrganisationPreview } from '../organisationPreview/OrganisationPreview'

import { emailRegex } from '@percent/utility'
import type { Organisation } from '@percent/partner-dashboard/services/nonprofits/nonprofitsService.types'
import { useServices } from '@percent/partner-dashboard/context/serviceContext/ServiceContext'
import { useCountries } from '@percent/partner-dashboard/common/hooks/useCountries/useCountries'
import {
  useAuthState,
  useMutation,
  useOrganisationLookup,
  useRequiredParams
} from '@percent/partner-dashboard/common/hooks'
import {
  capitalizeFirstLetter,
  DEFAULT_LOCALE,
  normalizeWebsite
} from '@percent/partner-dashboard/app/dashboard/grants/utils/utils'
import {
  AcknowledgeModal,
  ActionModal,
  AsyncSelect,
  Flex,
  FormField,
  Modal,
  Select,
  type SelectOption,
  Text,
  TextInput
} from '@percent/lemonade'

interface AddOrganisationModalProps {
  open: boolean
  setOpen: (open: boolean) => void
}

type AcknowledgeModalState = 'success' | 'error'

const initialFormValues = {
  country: '',
  organisationId: '',
  organizationName: '',
  manualOrgName: '',
  manualRegistry: '',
  manualRegistryId: '',
  manualWebsite: '',
  contactFirstName: '',
  contactEmail: ''
}

const getAddOrganisationFormSchema = (t: TFunction, isManualEntry: boolean) =>
  Yup.object({
    country: Yup.string().required(t('errorMessage.required') as string),
    ...(isManualEntry
      ? {
          manualOrgName: Yup.string()
            .trim()
            .required(t('errorMessage.required') as string),
          manualRegistry: Yup.string().required(t('errorMessage.required') as string),
          manualWebsite: Yup.string()
            .trim()
            .required(t('errorMessage.required') as string)
            .matches(/^[^@\s]+\.[^@\s]+$/, t('errorMessage.invalidWebsite') as string)
        }
      : {
          organisationId: Yup.string().required(t('errorMessage.required') as string)
        }),
    contactFirstName: Yup.string()
      .trim()
      .required(t('errorMessage.required') as string),
    contactEmail: Yup.string()
      .trim()
      .required(t('errorMessage.required') as string)
      .test('email-format', t('errorMessage.invalidEmail') as string, value => Boolean(value && emailRegex.test(value)))
  })

export function AddOrganisationModal({ open, setOpen }: Readonly<AddOrganisationModalProps>) {
  const { t } = useTranslation()
  const { id: programId } = useRequiredParams()
  const { user } = useAuthState()
  const { countries } = useCountries()
  const { grantsService } = useServices()
  const { program, candidates } = useCandidates()

  const programName = program.name

  const [modalState, setModalState] = useState<AcknowledgeModalState | null>(null)
  const [selectedOrganisation, setSelectedOrganisation] = useState<Organisation | null>(null)
  const [isManualEntry, setIsManualEntry] = useState(false)

  const countryOptions = useMemo(() => (countries ?? []).map(c => ({ value: c.code, label: c.name })), [countries])

  const validationSchema = useMemo(() => getAddOrganisationFormSchema(t, isManualEntry), [t, isManualEntry])

  const [{ isLoading: isSubmitting }, { apiFunc: submitNomination }] = useMutation(
    grantsService.submitNomination,
    () => setModalState('success'),
    () => setModalState('error')
  )

  const formik = useFormik({
    initialValues: initialFormValues,
    validationSchema,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: values => {
      submitNomination({
        programId,
        nomination: {
          nominator: {
            firstName: '', // Required by the API, empty string allowed by API validation schema
            lastName: '', // Required by the API, empty string allowed by API validation schema
            email: user?.email ?? ''
          },
          organisationContact: {
            firstName: capitalizeFirstLetter(values.contactFirstName),
            email: values.contactEmail.trim()
          }
        },
        country: values.country,
        organisation: isManualEntry
          ? {
              name: values.manualOrgName.trim(),
              country: values.country,
              registry: values.manualRegistry,
              registryId: values.manualRegistryId.trim() || undefined,
              website: normalizeWebsite(values.manualWebsite)
            }
          : { id: values.organisationId },
        locale: DEFAULT_LOCALE // English only program
      })
    }
  })

  const {
    values,
    errors,
    touched,
    handleSubmit,
    handleChange,
    handleBlur,
    setFieldValue,
    setFieldTouched,
    isValid,
    dirty,
    resetForm,
    validateForm
  } = formik

  useEffect(() => {
    validateForm()
  }, [isManualEntry, validateForm])

  const { orgSearchQuery, setOrgSearchQuery, orgSearchData, orgOptions, isOrgSearchLoading } = useOrganisationLookup(
    values.country
  )

  const { registryOptions, isRegistryLoading } = useRegistryLookup(values.country)

  const resetFormState = () => {
    resetForm({ values: initialFormValues })
    setSelectedOrganisation(null)
    setIsManualEntry(false)
    setOrgSearchQuery('')
    setModalState(null)
  }

  const handleClose = () => {
    if (modalState === 'success') {
      candidates.refresh()
    }

    setOpen(false)
    resetFormState()
  }

  const countryDefaultValue = values.country ? countryOptions.find(c => c.value === values.country) : undefined

  const hasFieldError = (field: string) => Boolean(touched[field] && errors[field])
  const fieldStatus = (hasError: boolean) => (hasError ? 'danger' : 'default')
  const fieldStatusMessage = (hasError: boolean, field: string) => (hasError ? errors[field] : undefined)

  const showCountryError = hasFieldError('country') && !values.country
  const showOrganisationError = !isManualEntry && hasFieldError('organisationId') && !values.organisationId
  const showContactFirstNameError = hasFieldError('contactFirstName') && !values.contactFirstName.trim()
  const showContactEmailError =
    hasFieldError('contactEmail') && (!values.contactEmail.trim() || !emailRegex.test(values.contactEmail))

  const handleCountryChange = (option: SelectOption) => {
    resetForm({
      values: {
        ...initialFormValues,
        country: option.value,
        contactFirstName: values.contactFirstName,
        contactEmail: values.contactEmail
      }
    })
    setFieldTouched('country', true)
    setSelectedOrganisation(null)
    setIsManualEntry(false)
    setOrgSearchQuery('')
  }

  const handleOrganisationChange = (option: SelectOption | null) => {
    if (option) {
      setFieldValue('organizationName', option.label, false)
      setFieldValue('organisationId', option.value)
      setFieldTouched('organisationId', true, false)
      setSelectedOrganisation(orgSearchData?.find(org => org.id === option.value) ?? null)
      setIsManualEntry(false)
    } else {
      setFieldValue('organisationId', '')
      setFieldValue('organizationName', '')
      setSelectedOrganisation(null)
      setOrgSearchQuery('')
    }
  }

  const handleAddOrganisation = () => {
    setSelectedOrganisation(null)
    setFieldValue('organisationId', '')
    setFieldValue('organizationName', '')
    setIsManualEntry(true)
  }

  const handleRegistryChange = (option: SelectOption) => {
    setFieldValue('manualRegistry', option.value)
    setFieldTouched('manualRegistry', true, false)
  }

  return (
    <Modal open={open} onClose={handleClose} showModalOverflow={false}>
      {modalState === 'success' && (
        <AcknowledgeModal
          result="positive"
          title={t('grants.candidates.addOrganisationModal.success.title')}
          description={t('grants.candidates.addOrganisationModal.success.description', {
            organization: isManualEntry ? values.manualOrgName : values.organizationName
          })}
          buttonText={t('button.close')}
          handleClose={handleClose}
        />
      )}
      {modalState === 'error' && (
        <AcknowledgeModal
          result="negative"
          title={t('grants.candidates.addOrganisationModal.error.title')}
          description={t('grants.candidates.addOrganisationModal.error.description', {
            organization: isManualEntry ? values.manualOrgName : values.organizationName,
            programName
          })}
          buttonText={t('button.close')}
          handleClose={handleClose}
        />
      )}
      {!modalState && (
        <ActionModal
          title={t('grants.candidates.addOrganisationModal.title')}
          primaryButtonText={t('grants.candidates.addOrganization.button')}
          secondaryButtonText={t('button.cancel')}
          type="submit"
          handleClose={handleClose}
          handleSubmit={handleSubmit}
          loading={isSubmitting}
          loadingText={t('grants.candidates.addOrganisationModal.button.loading')}
          disabled={isSubmitting || !isValid || !dirty}
          primaryBtnTestId="add-organisation-modal-button"
          secondaryBtnTestId="cancel-add-organisation-modal-button"
        >
          <form id="add-organisation-form" noValidate onSubmit={handleSubmit}>
            <Flex direction="column" gap={16}>
              <Text>{t('grants.candidates.addOrganisationModal.description')}</Text>
              <FormField
                label={t('grants.candidates.addOrganisationModal.country')}
                necessity="required"
                status={fieldStatus(showCountryError)}
                statusMessage={fieldStatusMessage(showCountryError, 'country')}
                data-testid="add-org-country-select"
              >
                <Select
                  key={values.country || 'country-empty'}
                  name="country"
                  placeholder={t('grants.candidates.addOrganisationModal.country.placeholder')}
                  options={countryOptions}
                  searchable
                  defaultValue={countryDefaultValue}
                  onChange={handleCountryChange}
                />
              </FormField>
              <FormField
                label={t('grants.candidates.addOrganisationModal.organisation')}
                necessity="required"
                status={fieldStatus(showOrganisationError)}
                statusMessage={fieldStatusMessage(showOrganisationError, 'organisationId')}
              >
                <Flex direction="column" gap={12} w="100%">
                  <AsyncSelect
                    key={values.country}
                    placeholder={t('grants.candidates.addOrganisationModal.organisation.placeholder')}
                    options={orgOptions}
                    query={values.organizationName || orgSearchQuery}
                    setQuery={setOrgSearchQuery}
                    loading={orgSearchQuery.length > 0 && isOrgSearchLoading && !values.organisationId}
                    loadingText={t('typography.searching')}
                    noResultsFoundText={t(
                      orgSearchQuery ? 'typography.noResultsFound' : 'typography.pleaseEnterSearchTerm'
                    )}
                    disabled={!values.country}
                    minSearchLength={3}
                    minSearchLengthMessage={t('typography.minSearchLengthMessage', { count: 3 })}
                    defaultValue={
                      values.organisationId && values.organizationName
                        ? {
                            value: values.organisationId,
                            label: values.organizationName
                          }
                        : undefined
                    }
                    onChange={handleOrganisationChange}
                    addOption={{
                      label: t('grants.candidates.addOrganisationModal.cantFind'),
                      onClick: handleAddOrganisation,
                      hideIcon: true
                    }}
                    data-testid="add-org-organisation-select"
                  />
                  {selectedOrganisation && !isManualEntry ? (
                    <OrganisationPreview organisation={selectedOrganisation} />
                  ) : null}
                </Flex>
              </FormField>
              {isManualEntry && (
                <>
                  <FormField
                    label={t('grants.candidates.addOrganisationModal.manualOrgName')}
                    necessity="required"
                    status={fieldStatus(hasFieldError('manualOrgName'))}
                    statusMessage={fieldStatusMessage(hasFieldError('manualOrgName'), 'manualOrgName')}
                    data-testid="add-org-manual-name-input"
                  >
                    <TextInput
                      name="manualOrgName"
                      value={values.manualOrgName}
                      placeholder={t('grants.candidates.addOrganisationModal.manualOrgName.placeholder')}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </FormField>
                  <FormField
                    label={t('grants.candidates.addOrganisationModal.manualRegistry')}
                    necessity="required"
                    status={fieldStatus(hasFieldError('manualRegistry'))}
                    statusMessage={fieldStatusMessage(hasFieldError('manualRegistry'), 'manualRegistry')}
                    data-testid="add-org-manual-registry-select"
                  >
                    <Select
                      placeholder={t('grants.candidates.addOrganisationModal.manualRegistry.placeholder')}
                      options={registryOptions}
                      disabled={!values.country}
                      isLoading={isRegistryLoading}
                      searchable
                      onChange={handleRegistryChange}
                      defaultValue={
                        values.manualRegistry === 'unknown-registry'
                          ? {
                              value: 'unknown-registry',
                              label: t('grants.candidates.addOrganisationModal.unknownRegistry')
                            }
                          : registryOptions.find(o => o.value === values.manualRegistry)
                      }
                      addOption={{
                        label: t('grants.candidates.addOrganisationModal.unknownRegistry'),
                        onClick: () =>
                          handleRegistryChange({
                            value: 'unknown-registry',
                            label: t('grants.candidates.addOrganisationModal.unknownRegistry')
                          }),
                        hideIcon: true
                      }}
                    />
                  </FormField>
                  <FormField
                    label={t('grants.candidates.addOrganisationModal.manualRegistryId')}
                    description={t('grants.candidates.addOrganisationModal.manualRegistryId.description')}
                    necessity="optional"
                    data-testid="add-org-manual-registry-id-input"
                  >
                    <TextInput
                      name="manualRegistryId"
                      value={values.manualRegistryId}
                      placeholder={t('grants.candidates.addOrganisationModal.manualRegistryId.placeholder')}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </FormField>
                  <FormField
                    label={t('grants.candidates.addOrganisationModal.manualWebsite')}
                    necessity="required"
                    status={fieldStatus(hasFieldError('manualWebsite'))}
                    statusMessage={fieldStatusMessage(hasFieldError('manualWebsite'), 'manualWebsite')}
                    data-testid="add-org-manual-website-input"
                  >
                    <TextInput
                      name="manualWebsite"
                      value={values.manualWebsite}
                      placeholder={t('grants.candidates.addOrganisationModal.manualWebsite.placeholder')}
                      onChange={handleChange}
                      onBlur={handleBlur}
                    />
                  </FormField>
                </>
              )}
              <FormField
                label={t('grants.candidates.addOrganisationModal.contactFirstName')}
                necessity="required"
                status={fieldStatus(showContactFirstNameError)}
                statusMessage={fieldStatusMessage(showContactFirstNameError, 'contactFirstName')}
                data-testid="add-org-contact-name-input"
              >
                <TextInput
                  name="contactFirstName"
                  value={values.contactFirstName}
                  placeholder={t('grants.candidates.addOrganisationModal.contactName.placeholder')}
                  onChange={handleChange}
                  onBlur={handleBlur}
                />
              </FormField>
              <FormField
                label={t('grants.candidates.addOrganisationModal.contactEmail')}
                description={
                  showContactEmailError
                    ? undefined
                    : t('grants.candidates.addOrganisationModal.contactEmail.description')
                }
                necessity="required"
                status={fieldStatus(showContactEmailError)}
                statusMessage={fieldStatusMessage(showContactEmailError, 'contactEmail')}
                data-testid="add-org-contact-email-input"
              >
                <TextInput
                  type="email"
                  autoComplete="email"
                  name="contactEmail"
                  value={values.contactEmail}
                  placeholder={t('grants.candidates.addOrganisationModal.contactEmail.placeholder')}
                  onChange={handleChange}
                  onBlur={e => {
                    handleBlur(e)
                    setFieldValue('contactEmail', e.target.value.trim())
                  }}
                />
              </FormField>
            </Flex>
          </form>
        </ActionModal>
      )}
    </Modal>
  )
}
